import React, { useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { createLeadGroup } from "../Services/APIService";

interface CreateLeadGroupCardProps {
  onCreateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  groupId: number | "";
  groupName: string;
  groupPurpose: string;
  tenantId: number;
  isActive: boolean;
}

const requiredField = <span className="text-danger ms-1">*</span>;

const CreateGroupCard: React.FC<CreateLeadGroupCardProps> = ({
  onCreateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    groupId: 0,
    groupName: "",
    groupPurpose: "",
    tenantId: 0,
    isActive: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.groupName.trim()) {
      setError("Please enter a Group Name");
      return;
    }

    try {
      setIsLoading(true);
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");
      if (!userId) {
        throw new Error("User ID not found in localStorage");
      }
      if (!tenantId) {
        throw new Error("Tenant ID not found in localStorage");
      }

      const payload = {
        groupName: formData.groupName.trim().replace(/\s+/g, " "),
        groupPurpose: formData.groupPurpose,
        tenantId: parseInt(tenantId),
        isActive: true,
        createdBy: parseInt(userId),
      };

      const data = await createLeadGroup(payload);

      if (data.isCreated) {
        toast.success("Lead Group Created Successfully!");
        setFormData({
          groupId: "",
          groupName: "",
          groupPurpose: "",
          tenantId: 0,
          isActive: true,
        });
        onCreateComplete();
      } else {
        throw new Error("Failed to create lead group");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      groupId: "",
      groupName: "",
      groupPurpose: "",
      tenantId: 0,
      isActive: true,
    });
    setError(null);
    onCancel();
  };

  return (
    <div className="card mb-4">
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create Lead Group</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger" role="alert">
              {error}
            </div>
          )}
          <div className="row">
            <div className="col-12 col-lg-4 mb-3">
              <label className="form-label">
                Lead Group Name{requiredField}
              </label>
              <input
                type="text"
                name="groupName"
                className="form-control form-control-lg"
                placeholder="Enter Group Name"
                value={formData.groupName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="col-12 col-lg-4 mb-3">
              <label className="form-label">
                Lead Group Purpose{requiredField}
              </label>
              <textarea
                onChange={handleChange}
                name="groupPurpose"
                className="form-control form-control-lg"
                placeholder="Enter Group Purpose"
                value={formData.groupPurpose}
                required
              />
            </div>
            <div className="col-12">
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Creating..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupCard;
